import { HttpClient } from '@angular/common/http';
import { plainToInstance } from 'class-transformer';

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
	WithdrawEditArgument,
	WithdrawStudentArgument,
} from 'src/lib/services/api/students/enrollment/withdraw-student.argument';
import { ExitReasonModel } from './student-enrollment-exit-reason.model';
import { StudentEnrollmentInfoOptionsResponseModel } from './student-enrollment-info-options-response.model';
import {
	EnrollmentInfoEditResponseModel,
	EnrollmentInfoPaperworkModel,
	EnrollmentInfoStudentDataModel,
} from './student-enrollment-info-response.model';

import {
	buildFormData,
	buildQueryString,
	HttpCallCache,
} from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { StudentEnrollmentInfoArgument } from './student-enrollment-info.argument';
import { UploadPaperworkArgument } from './student-enrollment-paperwork-option.model';
import { PaywhirlInfoModel } from './student-enrollment-payment.model';
import { StudentPaperworkUpdateArgument } from './student-paperwork-update.argument';
import {
	StudentGraduationWithdrawRequestArgumentsModel,
	StudentWithdrawRequestArgumentsModel,
	StudentWithdrawRequestModel,
} from './student-withdraw.model';

@Injectable({
	providedIn: 'root',
})
export class StudentsEnrollmentService {
	private getWithdrawRequest_HttpCallCache = new HttpCallCache<
		number,
		StudentWithdrawRequestModel
	>();

	private getEnrollmentFormData_HttpCallCache = new HttpCallCache<
		string,
		EnrollmentInfoStudentDataModel
	>();

	constructor(private httpClient: HttpClient) {}

	public getExitReasons = (uid: number): Observable<ExitReasonModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/students/${uid}/exit_reasons`)
			.pipe(
				mapResponse((r) => plainToInstance(ExitReasonModel, r), {
					errorCode: 'EBDADCFB',
				}),
			);
	};

	public withdrawSingleStudent = (
		uid: number,
		args: WithdrawStudentArgument,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/withdraw`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '5AB760E3',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public cancelWithdrawRequest = (uid: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/withdraw/cancel`, {})
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'E09B4745',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public getWithdrawRequest = (
		studentId: number,
		force: boolean = false,
	): Observable<StudentWithdrawRequestModel> => {
		if (force) this.getWithdrawRequest_HttpCallCache.flushAll();

		return this.getWithdrawRequest_HttpCallCache.fetch$(
			studentId,
			this.httpClient
				.get<{
					withdraw_request: StudentWithdrawRequestModel;
					success: boolean;
				}>(`/api/v1/students/${studentId}/withdraw/request`)
				.pipe(
					mapResponse(
						(r) =>
							plainToInstance(StudentWithdrawRequestModel, r.withdraw_request),
						{
							errorCode: '7DF5561D',
							errorMessage: DefaultErrorMessage.Saving,
						},
					),
				),
		);
	};

	public editWithdrawRequest = (
		studentId: number,
		requestId: number,
		args: StudentWithdrawRequestArgumentsModel,
	) => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/withdraw/request/${requestId}`,
				args,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '69826EC3',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editWithdrawSingleStudent = (
		uid: number,
		args: WithdrawEditArgument,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/withdraw/edit`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'FA485584',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Enrollment Form Info
	public getEnrollmentFormData = (
		studentId: number,
		includeExpiredPaperwork?: boolean,
	): Observable<EnrollmentInfoStudentDataModel> => {
		const qs = buildQueryString({
			include_expired_paperwork: includeExpiredPaperwork || undefined,
		});

		return this.getEnrollmentFormData_HttpCallCache.fetch$(
			`${studentId}.${includeExpiredPaperwork}`,
			this.httpClient
				.get<{
					student_data: unknown;
				}>(`/api/v1/students/${studentId}/enrollment/form${qs}`)
				.pipe(
					mapResponse(
						(r) =>
							plainToInstance(EnrollmentInfoStudentDataModel, r.student_data),
						{
							errorCode: '186E3DDD',
						},
					),
				),
		);
	};

	public getEnrollmentFormDataOptions = (
		studentId: number,
	): Observable<StudentEnrollmentInfoOptionsResponseModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/students/${studentId}/enrollment/form/options`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(StudentEnrollmentInfoOptionsResponseModel, r),
					{ errorCode: '8CFAE5C4' },
				),
			);
	};

	public setEnrollmentFormData = (
		studentId: number,
		args: StudentEnrollmentInfoArgument,
	): Observable<EnrollmentInfoEditResponseModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${studentId}/enrollment/form`, {
				student_data: args,
			})
			.pipe(
				mapResponse(
					(r) => plainToInstance(EnrollmentInfoEditResponseModel, r),
					{
						errorCode: '0F67D15D',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	/* Paperwork */
	public getPaperwork = (
		studentId: number,
		includeExpiredPaperwork?: boolean,
	): Observable<EnrollmentInfoPaperworkModel[]> => {
		const qs = buildQueryString({
			include_expired_paperwork: includeExpiredPaperwork || undefined,
		});

		return this.httpClient
			.get<{
				paperwork: unknown[];
			}>(`/api/v1/students/${studentId}/enrollment/form/paperwork${qs}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(EnrollmentInfoPaperworkModel, r.paperwork),
					{ errorCode: 'E95CB49D' },
				),
			);
	};

	public getUploadPaperworkOptions = (
		studentId: number,
	): Observable<string[]> => {
		return this.httpClient
			.get<{
				paperwork_types: string[];
			}>(`/api/v1/students/${studentId}/enrollment/form/paperwork/options`)
			.pipe(
				mapResponse((r) => r.paperwork_types, {
					errorCode: 'ED1C9A6E',
				}),
			);
	};

	public uploadPaperwork = (
		studentId: number,
		args: UploadPaperworkArgument,
		directUploadConfirmation: string | null,
	): Observable<EnrollmentInfoPaperworkModel> => {
		const bodyArgs = {};
		bodyArgs['jsonBody'] = args;
		if (directUploadConfirmation) {
			bodyArgs['__direct_file_upload__file'] = directUploadConfirmation;
		}

		const formData = buildFormData(bodyArgs);
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/enrollment/form/paperwork`,
				formData,
			)
			.pipe(
				mapResponse((r) => plainToInstance(EnrollmentInfoPaperworkModel, r), {
					errorCode: 'CB04DEC5',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public updatePaperwork = (
		studentId: number,
		args: StudentPaperworkUpdateArgument,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/enrollment/form/paperwork/update`,
				args,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'CE673755',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public expirePaperwork = (
		studentId: number,
		fileId: string,
		type?: string,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/enrollment/form/paperwork/expire`,
				{ file_id: fileId, expire_waived_type: type },
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'EA3310FE',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deletePaperwork = (
		studentId: number,
		fileId: string,
		type?: string,
	): Observable<{
		success: boolean;
	}> => {
		return this.httpClient
			.post<{
				success: boolean;
			}>(`/api/v1/students/${studentId}/enrollment/form/paperwork/delete`, {
				file_id: fileId,
				delete_waived_type: type,
			})
			.pipe(
				mapResponse((r) => r, {
					errorCode: '67C61ED5',
					errorMessage: 'There was an issue deleting the paperwork',
				}),
			);
	};

	public removePaperworkExpiration = (
		uid: number,
		fileId: string,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${uid}/enrollment/form/paperwork/remove_expiration`,
				{ file_id: fileId },
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '67C61ED5',
					errorMessage: 'There was an issue removing the paperwork expiration',
				}),
			);
	};

	/* Paperwork Tags */
	public getPaperworkTagOptions = (): Observable<string[]> => {
		return this.httpClient.get<unknown>(`/api/v1/paperwork/tags`).pipe(
			mapResponse((r) => r as string[], {
				errorCode: 'D9412949',
			}),
		);
	};

	public createPaperworkTag = (tags: string[]): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/paperwork/tags/add`, { tags })
			.pipe(
				mapResponse((r) => r, {
					errorCode: '270738F5',
				}),
			);
	};

	public uploadTranscript = (
		studentId: number,
		directUploadConfirmation: string,
	) => {
		const bodyArgs = {};
		bodyArgs[' __direct_file_upload__transcript_file'] =
			directUploadConfirmation;

		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/transcript/transferred`,
				buildFormData(bodyArgs),
			)
			.pipe(
				mapResponse((r) => plainToInstance(EnrollmentInfoPaperworkModel, r), {
					errorCode: '2D08F518',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/* TAA Payment */
	public getPaywhirlInformation = (
		studentId: number,
	): Observable<PaywhirlInfoModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/students/${studentId}/billing/paywhirl`)
			.pipe(
				mapResponse((r) => plainToInstance(PaywhirlInfoModel, r), {
					errorCode: '49684C08',
				}),
			);
	};

	public setPaywhirlInformation = (
		studentId: number,
		customerId: number,
		ignore: boolean,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${studentId}/billing/paywhirl`, {
				ignore_payment_status: ignore,
				customer_id: customerId,
			})
			.pipe(
				mapResponse((r) => r, {
					errorCode: '76EC64AD',
					errorMessage: 'There was an issue updating the status',
				}),
			);
	};

	public createWithdrawRequest = (
		studentId: number,
		args: StudentWithdrawRequestArgumentsModel,
	) => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${studentId}/withdraw/request`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '18D91F20',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public authorizeWithdrawRequest = (
		studentId: number,
		requestId: number,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/withdraw/request/${requestId}/authorize`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '7B8E9DFA',
				}),
			);
	};

	public approveWithdrawRequest = (
		studentId: number,
		requestId: number,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/withdraw/request/${requestId}/approve`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '6461BA20',
				}),
			);
	};

	public rejectWithdrawRequest = (
		studentId: number,
		requestId: number,
		reason: string,
	) => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/withdraw/request/${requestId}/reject`,
				{
					reason: reason,
				},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '892E4A2C',
				}),
			);
	};

	public createGraduationWithdrawRequest = (
		uid: number,
		args: StudentGraduationWithdrawRequestArgumentsModel,
	) => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${uid}/graduation/submit_graduation`,
				args,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'DDC62810',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};
}
